import React from 'react';
import { Badge } from '@vaiu/ui/badge';
import { cn } from '@vaiu/utils';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertTriangle,
  Loader2
} from 'lucide-react';

interface StatusBadgeProps {
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'active' | 'inactive' | 'verified' | 'unverified' | 'expired';
  text?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  text,
  showIcon = true,
  size = 'md',
  className
}) => {
  const getStatusConfig = (status: string) => {
    const configs = {
      pending: {
        variant: 'secondary' as const,
        icon: Clock,
        text: 'Pending',
        className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
      },
      completed: {
        variant: 'default' as const,
        icon: CheckCircle,
        text: 'Completed',
        className: 'bg-green-100 text-green-800 hover:bg-green-100'
      },
      failed: {
        variant: 'destructive' as const,
        icon: XCircle,
        text: 'Failed',
        className: 'bg-red-100 text-red-800 hover:bg-red-100'
      },
      cancelled: {
        variant: 'secondary' as const,
        icon: XCircle,
        text: 'Cancelled',
        className: 'bg-gray-100 text-gray-800 hover:bg-gray-100'
      },
      active: {
        variant: 'default' as const,
        icon: CheckCircle,
        text: 'Active',
        className: 'bg-green-100 text-green-800 hover:bg-green-100'
      },
      inactive: {
        variant: 'secondary' as const,
        icon: Clock,
        text: 'Inactive',
        className: 'bg-gray-100 text-gray-800 hover:bg-gray-100'
      },
      verified: {
        variant: 'default' as const,
        icon: CheckCircle,
        text: 'Verified',
        className: 'bg-blue-100 text-blue-800 hover:bg-blue-100'
      },
      unverified: {
        variant: 'secondary' as const,
        icon: AlertTriangle,
        text: 'Unverified',
        className: 'bg-orange-100 text-orange-800 hover:bg-orange-100'
      },
      expired: {
        variant: 'secondary' as const,
        icon: Clock,
        text: 'Expired',
        className: 'bg-gray-50 text-gray-700 border-gray-200'
      }
    };

    return configs[status as keyof typeof configs] || configs.pending;
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;
  const displayText = text || config.text;
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  return (
    <Badge 
      variant={config.variant}
      className={cn(
        config.className,
        size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-0.5',
        'flex items-center space-x-1',
        className
      )}
    >
      {showIcon && <Icon className={iconSize} />}
      <span>{displayText}</span>
    </Badge>
  );
};

export default StatusBadge; 